import type { ReactNode } from 'react'

import { Placeholder } from '@/src/elements/placeholder'
import type { GENERAL_NOTIFICATION_SETTINGS } from '@/src/features/notification/models/notification.constants'
import { useNotificationSettingsMutation } from '@/src/features/notification/services/useNotificationSettingsMutation'
import { useNotificationSettingsQuery } from '@/src/features/notification/services/useNotificationSettingsQuery'
import { useAsyncToggle } from '@/src/general/hooks/useAsyncToggle'

import { SettingsCardGeneralBase, type TSettingsCardGeneralBaseProps } from './SettingsCardGeneralBase'

type TSettingsCardGeneralProps = Pick<TSettingsCardGeneralBaseProps, 'title' | 'testID'> & {
  settingsKey: GENERAL_NOTIFICATION_SETTINGS
  withPush?: boolean
  updateParametersToken?: string
}

export const SettingsCardGeneral = ({
  updateParametersToken,
  settingsKey,
  withPush,
  ...otherProps
}: TSettingsCardGeneralProps): ReactNode => {
  const { data, isError } = useNotificationSettingsQuery(
    { isSubscriptionAlive: true, updateParametersToken },
    !!updateParametersToken,
  )
  const { mutateAsync } = useNotificationSettingsMutation(settingsKey, updateParametersToken)

  const [isMailEnabled, onMailChange, mailDisabled] = useAsyncToggle(data?.[settingsKey]?.email, async (email) => {
    await mutateAsync({ email })
  })
  const [isPushEnabled, onPushChange, pushDisabled] = useAsyncToggle(data?.[settingsKey]?.push, async (pushValue) => {
    await mutateAsync({ push: pushValue })
  })

  if (isError) return null

  if (!data) return <Placeholder className="h-40" />

  return (
    <SettingsCardGeneralBase
      isMailEnabled={isMailEnabled}
      onMailChange={onMailChange}
      mailDisabled={mailDisabled}
      {...(withPush && { isPushEnabled, onPushChange, pushDisabled })}
      {...otherProps}
    />
  )
}
